import { useEffect, useRef } from 'react';

// Ticket 154: visibility-aware poller shared by the live Inbox screens.
// Ticks only while the tab is visible (a hidden tab must not hit the API),
// calls once on return to visible, and never overlaps a slow request.
// `immediate: false` is for callers that just did their own first load.
// Ticket 154 P3: `hiddenMs > 0` keeps a hidden tab ticking at that slower
// cadence (browser notifications are useless if a background tab never polls).
export default function usePolling(fn, ms, { enabled = true, immediate = true, hiddenMs = 0 } = {}) {
  // Read through a ref so a fresh closure each render never restarts the timer.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    let pending = false;
    const isVisible = () => document.visibilityState !== 'hidden';

    const run = () => {
      if (pending) return;
      pending = true;
      let p;
      try { p = fnRef.current(); } catch { p = null; }
      Promise.resolve(p).catch(() => {}).finally(() => { pending = false; });
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const start = (every = ms) => { stop(); timer = setInterval(run, every); };
    const onVisibility = () => {
      if (isVisible()) { run(); start(); } else if (hiddenMs > 0) start(hiddenMs); else stop();
    };

    if (isVisible()) {
      if (immediate) run();
      start();
    } else if (hiddenMs > 0) {
      start(hiddenMs);
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [ms, enabled, immediate, hiddenMs]);
}
